import { createApi, fetchBaseQuery, retry } from '@reduxjs/toolkit/query/react';
import { EntitlementSlugEnum } from '@electricjs/core_entity-client';

import { RootState } from '@/redux/store';
import { entitlementMiddleware } from '@/redux/middleware/entitlementMiddleware';
import {
  AddSoftwareAppArgs,
  DeleteSystemBySerialNumberArgs,
  GetOrgSoftwareAppsArgs,
  GetSoftwareCatalogArgs,
  JumpCloudAppleMdmProvisionArgs,
  LockArgs,
  MDMInformation,
  OrgSoftwareApp,
  ProvisionStatusEnum,
  RemoveSoftwareAppArgs,
  SoftwareCatalogApp,
  UnenrollDeviceArgs,
  WipeArgs,
  GetSystemRecoveryKeyArgs,
  SystemRecoveryKeyResponse,
  SystemWithPolicyStats,
} from '@/types/mdmManagement';

import retryCondition from './helpers/retryCondition';
import { fetchAndDownloadFile } from './helpers/fetchAndDownloadFileHelper';
import { TagTypes } from '@/redux/slices/apiSlice';
import {
  MDMInformation as ResultMDMInformation,
  SystemWithPolicyStatsResponseModel,
} from '@electricjs/mdm_management-client';

/**
 * Defining a new API because MDM management come from a new baseUrl.
 */
export const mdmManagementSlice = createApi({
  reducerPath: 'mdmManagementSlice',
  baseQuery: entitlementMiddleware(
    retry(
      fetchBaseQuery({
        baseUrl: window._env_?.VITE_MDM_MANAGEMENT_BASE_URL,
        prepareHeaders: (headers, api) => {
          const accessToken = (api.getState() as RootState).loggedUser
            ?.accessToken;
          if (accessToken) {
            headers.set('authorization', `Bearer ${accessToken}`);
          }
          return headers;
        },
      }),
      {
        retryCondition,
      }
    )
  ),
  endpoints: builder => ({
    getJumpcloudProvisionStatus: builder.query({
      query: organizationId => ({
        url: `jumpcloud/provision/${organizationId}`,
        meta: { entitlements: [EntitlementSlugEnum.JumpcloudMdm] },
      }),
      transformResponse: (status: ProvisionStatusEnum) => {
        return status;
      },
      transformErrorResponse: response => {
        return response;
      },
    }),
    lockDevice: builder.mutation<void, LockArgs>({
      query: ({ organizationId, deviceId, serialNumber, lockMethod }) => ({
        url: `jumpcloud/${organizationId}/device/${deviceId}/lock`,
        method: 'POST',
        body: {
          serial_number: serialNumber,
        },
        params: {
          lock_method: lockMethod,
        },
        meta: { entitlements: [EntitlementSlugEnum.JumpcloudMdm] },
      }),
    }),
    wipeDevice: builder.mutation<void, WipeArgs>({
      query: ({ organizationId, deviceId, wipeMethod }) => ({
        url: `jumpcloud/${organizationId}/device/${deviceId}/wipe`,
        method: 'POST',
        params: {
          wipe_method: wipeMethod,
        },
        meta: { entitlements: [EntitlementSlugEnum.JumpcloudMdm] },
      }),
    }),
    removeSystemBySerial: builder.mutation<
      void,
      DeleteSystemBySerialNumberArgs
    >({
      query: ({ organizationId, serialNumber }) => ({
        url: `jumpcloud/${organizationId}/systemsbyserial/${serialNumber}`,
        method: 'DELETE',
        meta: { entitlements: [EntitlementSlugEnum.JumpcloudMdm] },
      }),
    }),
    unenrollDevice: builder.mutation<void, UnenrollDeviceArgs>({
      query: ({ organizationId, deviceId }) => ({
        url: `jumpcloud/${organizationId}/device/${deviceId}/unenroll`,
        method: 'POST',
        meta: { entitlements: [EntitlementSlugEnum.JumpcloudMdm] },
      }),
    }),
    getJumpcloudDarwinDownloadLink: builder.query({
      query: organizationId => ({
        url: `jumpcloud/${organizationId}/darwin/download-link`,
        meta: { entitlements: [EntitlementSlugEnum.JumpcloudMdm] },
      }),
      transformErrorResponse: response => {
        response;
      },
    }),
    jumpCloudAppleMdmProvision: builder.mutation<
      void,
      JumpCloudAppleMdmProvisionArgs
    >({
      query: ({
        organizationId,
        appleCertCreatorAppleId,
        appleSignedCert,
        dep,
        encryptedDepServerToken,
        allowMobileUserEnrollment,
        defaultIosUserEnrollmentDeviceGroupId,
        defaultSystemGroupId,
        name,
      }) => ({
        url: `jumpcloud/${organizationId}/applemdms`,
        method: 'PUT',
        body: {
          apple_cert_creator_apple_id: appleCertCreatorAppleId,
          apple_signed_cert: appleSignedCert,
          dep: dep,
          encrypted_dep_server_token: encryptedDepServerToken,
          allow_mobile_user_enrollment: allowMobileUserEnrollment,
          default_ios_user_enrollment_device_group_id:
            defaultIosUserEnrollmentDeviceGroupId,
          default_system_group_id: defaultSystemGroupId,
          name: name,
        },
        meta: { entitlements: [EntitlementSlugEnum.JumpcloudMdm] },
      }),
      invalidatesTags: (_result, _error, { organizationId }) => [
        { type: TagTypes.MDMInformation, id: organizationId },
      ],
    }),
    getJumpCloudAppleCsr: builder.query<
      void,
      { organizationId: string; fileName?: string }
    >({
      queryFn: async (
        { organizationId, fileName },
        _api,
        _extraOptions,
        baseQuery
      ) => {
        const url = `jumpcloud/${organizationId}/applemdms/csr`;

        // Create a custom baseQuery wrapper that adds entitlements
        const baseQueryWithEntitlements = (queryArgs: unknown) => {
          if (typeof queryArgs === 'object' && queryArgs !== null) {
            return baseQuery({
              ...queryArgs,
              meta: { entitlements: [EntitlementSlugEnum.JumpcloudMdm] },
            });
          }
          return baseQuery(queryArgs);
        };

        return await fetchAndDownloadFile(
          baseQueryWithEntitlements,
          url,
          fileName
        );
      },
    }),
    getMDMInformation: builder.query<
      MDMInformation,
      { organizationId: string }
    >({
      query: ({ organizationId }) => ({
        url: `jumpcloud/${organizationId}/mdm-information`,
        meta: { entitlements: [EntitlementSlugEnum.JumpcloudMdm] },
      }),
      providesTags: (_result, _error, args) => [
        { type: TagTypes.MDMInformation, id: args.organizationId },
      ],
      transformResponse: (result: ResultMDMInformation): MDMInformation => {
        // TODO(HY-573): The generated mdm-management client has string types as any instead of string
        return {
          mdmOrganizationId: result.mdm_organization_id as string,
          appleCertificate: result.apple_certificate
            ? {
                creatorAppleId: result.apple_certificate
                  .creator_apple_id as string,
                serialNumber: result.apple_certificate.serial_number as string,
                expiresAt: result.apple_certificate.expires_at as string,
              }
            : undefined,
          appleMdmDepInfo: result.apple_mdm_dep_info
            ? {
                depServerTokenState: result.apple_mdm_dep_info
                  .dep_server_token_state as string,
                expiresAt: result.apple_mdm_dep_info
                  .dep_access_token_expiry as string,
              }
            : undefined,
        };
      },
    }),
    getSystemRecoveryKey: builder.query<
      SystemRecoveryKeyResponse,
      GetSystemRecoveryKeyArgs
    >({
      query: ({ organizationId, serialNumber }) => ({
        url: `jumpcloud/${organizationId}/serialnumber/${serialNumber}/recovery-key`,
        meta: { entitlements: [EntitlementSlugEnum.JumpcloudMdm] },
      }),
      transformResponse: (result: {
        recovery_key: string;
      }): SystemRecoveryKeyResponse => {
        return {
          recoveryKey: result.recovery_key,
        };
      },
    }),
    getSystemWithPolicyStatsBySerialNumber: builder.query<
      SystemWithPolicyStats,
      { organizationId: string; serialNumber: string }
    >({
      query: ({ organizationId, serialNumber }) => ({
        url: `jumpcloud/${organizationId}/systemsbyserial/${serialNumber}`,
        meta: { entitlements: [EntitlementSlugEnum.JumpcloudMdm] },
      }),
      providesTags: (_result, _error, args) => [
        { type: TagTypes.MDMSystemWithPolicyStats, id: args.serialNumber },
      ],
      transformResponse: (result: SystemWithPolicyStatsResponseModel) => {
        const osVersionDetailDto = result.system?.os_version_detail as
          | Record<string, string | number | undefined>
          | undefined;

        const policyStatsDto = result.policy_stats as Record<string, unknown>;
        const policyCountDataDto = policyStatsDto?.policy_count_data as Record<
          string,
          unknown
        >;

        return {
          system: {
            id: result.system?.id,
            active: result.system?.active,
            agentVersion: result.system?.agent_version,
            allowMultiFactorAuth: result.system?.allow_multi_factor_auth,
            allowPublicKeyAuth: result.system?.allow_public_key_auth,
            allowSshPasswordAuth: result.system?.allow_ssh_password_auth,
            allowSshRootLogin: result.system?.allow_ssh_root_login,
            arch: result.system?.arch,
            azureAdJoined: result.system?.azure_ad_joined,
            connectionHistory: result.system?.connection_history,
            created: result.system?.created,
            description: result.system?.description,
            displayName: result.system?.display_name,
            domainInfo: result.system?.domain_info,
            fde: result.system?.fde,
            fileSystem: result.system?.file_system,
            hasServiceAccount: result.system?.has_service_account,
            hostname: result.system?.hostname,
            lastContact: result.system?.last_contact,
            mdm: result.system?.mdm,
            modifySshdConfig: result.system?.modify_sshd_config,
            networkInterfaces: result.system?.network_interfaces,
            organization: result.system?.organization,
            os: result.system?.os,
            osFamily: result.system?.os_family,
            osVersionDetail: osVersionDetailDto
              ? {
                  distributionName:
                    (osVersionDetailDto?.distribution_name as string) ??
                    undefined,
                  major: (osVersionDetailDto?.major as string) ?? undefined,
                  majorNumber:
                    (osVersionDetailDto?.major_number as number) ?? undefined,
                  minor: (osVersionDetailDto?.minor as string) ?? undefined,
                  minorNumber:
                    (osVersionDetailDto?.minor_number as number) ?? undefined,
                  osName: (osVersionDetailDto?.os_name as string) ?? undefined,
                  patch: (osVersionDetailDto?.patch as string) ?? undefined,
                  patchNumber:
                    (osVersionDetailDto?.patch_number as number) ?? undefined,
                  releaseName:
                    (osVersionDetailDto?.release_name as string) ?? undefined,
                  revision:
                    (osVersionDetailDto?.revision as string) ?? undefined,
                  version: (osVersionDetailDto?.version as string) ?? undefined,
                }
              : undefined,
            remoteIp: result.system?.remote_ip,
            serialNumber: result.system?.serial_number,
            serviceAccountState: result.system?.service_account_state,
            sshRootEnabled: result.system?.ssh_root_enabled,
            sshdParams: result.system?.sshd_params,
            systemInsights: result.system?.system_insights,
            systemTimezone: result.system?.system_timezone,
            templateName: result.system?.template_name,
            usernameHashes: result.system?.username_hashes,
            userMetrics: result.system?.user_metrics,
            version: result.system?.version,
          },
          policyStats: policyStatsDto
            ? {
                failedPolicies:
                  (policyStatsDto.failed_policies as string[]) ?? undefined,
                pendingPolicies:
                  (policyStatsDto.pending_policies as string[]) ?? undefined,
                policyCountData: policyCountDataDto
                  ? {
                      total: (policyCountDataDto.total as number) ?? undefined,
                      success:
                        (policyCountDataDto.success as number) ?? undefined,
                      failed:
                        (policyCountDataDto.failed as number) ?? undefined,
                      pending:
                        (policyCountDataDto.pending as number) ?? undefined,
                      duplicate:
                        (policyCountDataDto.duplicate as number) ?? undefined,
                      unsupportedOs:
                        (policyCountDataDto.unsupportedOs as number) ??
                        undefined,
                    }
                  : undefined,
              }
            : undefined,
        };
      },
    }),
    // ─── Software Catalog Endpoints ─────────────────────────────────────────────
    getSoftwareCatalogApps: builder.query<
      SoftwareCatalogApp[],
      GetSoftwareCatalogArgs
    >({
      query: ({ organizationId, osType, query, limit = 25, skip = 0 }) => ({
        url: `jumpcloud/${organizationId}/software-catalog/apps`,
        params: {
          os_type: osType,
          ...(query ? { query } : {}),
          limit,
          skip,
        },
        meta: { entitlements: [EntitlementSlugEnum.JumpcloudMdm] },
      }),
      providesTags: [TagTypes.SoftwareCatalogApps],
    }),
    getOrgSoftwareApps: builder.query<OrgSoftwareApp[], GetOrgSoftwareAppsArgs>(
      {
        query: ({ organizationId }) => ({
          url: `jumpcloud/${organizationId}/software-apps`,
          meta: { entitlements: [EntitlementSlugEnum.JumpcloudMdm] },
        }),
        providesTags: [TagTypes.OrgSoftwareApps],
      }
    ),
    addSoftwareApp: builder.mutation<void, AddSoftwareAppArgs>({
      query: ({ organizationId, softwareAppId, osType }) => ({
        url: `jumpcloud/${organizationId}/software-apps`,
        method: 'POST',
        body: {
          software_app_id: softwareAppId,
          os_type: osType,
        },
        meta: { entitlements: [EntitlementSlugEnum.JumpcloudMdm] },
      }),
      invalidatesTags: [TagTypes.OrgSoftwareApps, TagTypes.SoftwareCatalogApps],
    }),
    removeSoftwareApp: builder.mutation<void, RemoveSoftwareAppArgs>({
      query: ({ organizationId, softwareAppId }) => ({
        url: `jumpcloud/${organizationId}/software-apps/${softwareAppId}`,
        method: 'DELETE',
        meta: { entitlements: [EntitlementSlugEnum.JumpcloudMdm] },
      }),
      invalidatesTags: [TagTypes.OrgSoftwareApps, TagTypes.SoftwareCatalogApps],
    }),
  }),
  tagTypes: [
    TagTypes.MDMInformation,
    TagTypes.MDMSystemWithPolicyStats,
    TagTypes.SoftwareCatalogApps,
    TagTypes.OrgSoftwareApps,
  ],
});

// export auto-generated hooks
export const {
  useGetMDMInformationQuery,
  useGetJumpcloudProvisionStatusQuery,
  useLazyGetJumpcloudProvisionStatusQuery,
  useLockDeviceMutation,
  useWipeDeviceMutation,
  useRemoveSystemBySerialMutation,
  useUnenrollDeviceMutation,
  useGetJumpcloudDarwinDownloadLinkQuery,
  useJumpCloudAppleMdmProvisionMutation,
  useLazyGetJumpCloudAppleCsrQuery,
  useLazyGetSystemRecoveryKeyQuery,
  useGetSystemWithPolicyStatsBySerialNumberQuery,
  useGetSoftwareCatalogAppsQuery,
  useGetOrgSoftwareAppsQuery,
  useAddSoftwareAppMutation,
  useRemoveSoftwareAppMutation,
} = mdmManagementSlice;
